import { useCallback, useContext, useEffect, useRef } from 'react';

import {
  POPUP_EVENT_NAME,
  PopupAction,
  PopupContext,
  PopupData,
  PopupEventPayload,
  PopupType,
  REGISTER_POPUPS,
} from 'contexts/PopupContext';

export const usePopup = (name: string) => {
  const handleAction = useRef<PopupAction<PopupType>>();

  if (!REGISTER_POPUPS.find((popup) => popup.name === name)) {
    throw new Error(`${name} not register in PopupContext`);
  }

  const {
    handleOpenPopupForce,
    handleOpenPopupASAP,
    handleUpdatePopupData,
    handleClosePopup,
    isAnyPopupOpen,
    checkIsPopupOpen,
    checkIsPopupInQueue,
    removePopupFromQueue,
  } = useContext(PopupContext);

  const handleOpenForce = useCallback(
    (data: PopupData<PopupType>) => {
      handleOpenPopupForce({
        name,
        data,
      });
    },
    [handleOpenPopupForce, name]
  );

  const handleOpenASAP = (data: PopupData<PopupType>) => {
    handleOpenPopupASAP({
      name,
      data,
    });
  };

  const handleUpdateData = (data: Partial<PopupData<PopupType>>) => {
    handleUpdatePopupData({
      name,
      data,
    });
  };

  const handleRemoveFromQueue = () => {
    removePopupFromQueue(name);
  };

  const checkIsOpen = useCallback(() => {
    return checkIsPopupOpen(name);
  }, [checkIsPopupOpen, name]);

  const checkIsInQueue = () => {
    return checkIsPopupInQueue(name);
  };

  const checkIsOpenOrInQueue = () => {
    return checkIsOpen() || checkIsInQueue();
  };

  useEffect(() => {
    const handlePopupActionEvent = (e: CustomEvent<PopupEventPayload>) => {
      if (
        e.detail.popup === name &&
        handleAction.current &&
        typeof handleAction.current === 'function'
      ) {
        handleAction.current(e.detail.action);
      }
    };

    document.addEventListener(
      POPUP_EVENT_NAME,
      handlePopupActionEvent as EventListener
    );

    return () => {
      document.removeEventListener(
        POPUP_EVENT_NAME,
        handlePopupActionEvent as EventListener
      );
    };
  }, [name]);

  return {
    handleOpenForce,
    handleOpenASAP,
    handleUpdateData,
    handleClose: handleClosePopup,
    handleRemoveFromQueue,
    handleAction,
    isAnyPopupOpen,
    checkIsOpen,
    checkIsInQueue,
    checkIsOpenOrInQueue,
  };
};
